import { useEffect, useState } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { Toaster } from './components/ui/toaster'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import ApplicationsPage from './pages/ApplicationsPage'
import ProductsPage from './pages/ProductsPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailPage from './pages/OrderDetailPage'
import SubscriptionsPage from './pages/SubscriptionsPage'
import SubscriptionDetailPage from './pages/SubscriptionDetailPage'
import PaymentConfigsPage from './pages/PaymentConfigsPage'
import PaymentsPage from './pages/PaymentsPage'
import RequestLogsPage from './pages/RequestLogsPage'
import AccountsPage from './pages/AccountsPage'
import AccountDetailPage from './pages/AccountDetailPage'
import DemoIntegrationPage from './pages/DemoIntegrationPage'

/**
 * Whether the backend has the demo integration module enabled.
 *
 * openspec change `demo-integration-and-channel-selectable-topup` (design
 * D5/D7): the demo blueprint is not registered when the module is off, so the
 * page must not be routed either — otherwise an operator would land on a
 * surface whose every call 404s. `null` means "not answered yet"; the route
 * stays absent until the backend actually says it is on.
 */
function useDemoIntegrationEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/v1/demo-status')
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setEnabled(Boolean(body?.demo_integration_enabled))
      })
      .catch(() => {
        // Unreachable status endpoint means "assume off": routing a demo page
        // we cannot confirm is worse than not offering it.
        if (!cancelled) setEnabled(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return enabled
}

function App() {
  const demoEnabled = useDemoIntegrationEnabled()

  return (
    <AuthProvider>
      <Toaster />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route
            path="applications"
            element={
              <ProtectedRoute requiredRole="admin">
                <ApplicationsPage />
              </ProtectedRoute>
            }
          />
          <Route path="products" element={<ProductsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="request-logs" element={<ProtectedRoute requiredRole="admin"><RequestLogsPage /></ProtectedRoute>} />
          {/*
            Accounts is gated exactly as Payments is — inside the authenticated
            shell, with no per-route role wrapper. Hiding the navigation entry
            from other roles is a convenience; the server's own checks are the
            boundary (the operator ledger refuses a tenant the caller may not
            read with 403 application_forbidden).
          */}
          <Route path="accounts" element={<AccountsPage />} />
          <Route path="accounts/:accountId" element={<AccountDetailPage />} />
          <Route path="orders/:orderId" element={<OrderDetailPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="subscriptions/:subscriptionId" element={<SubscriptionDetailPage />} />
          <Route
            path="payment-configs"
            element={
              <ProtectedRoute requiredRole="admin">
                <PaymentConfigsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="applications/:appId/payment-configs"
            element={
              <ProtectedRoute requiredRole="admin">
                <PaymentConfigsPage />
              </ProtectedRoute>
            }
          />
          {demoEnabled === true && (
            <Route
              path="demo"
              element={
                <ProtectedRoute requiredRole="admin">
                  <DemoIntegrationPage />
                </ProtectedRoute>
              }
            />
          )}
        </Route>
      </Routes>
    </AuthProvider>
  )
}

export default App
